import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const propertyTypes = ['Detached House', 'Semi-Detached', 'Townhouse', 'Condominium', 'Other'];
const timeframes = ['Within 1 month', '1–3 months', '3–6 months', '6–12 months', 'Just exploring options'];

const factors = [
  { icon: 'ri-map-pin-line', title: 'Location', desc: 'Neighbourhood desirability, nearby amenities, school districts and future development plans all play a role.' },
  { icon: 'ri-building-line', title: 'Property Condition', desc: 'Age, maintenance history, recent updates and overall presentation affect how buyers perceive value.' },
  { icon: 'ri-line-chart-line', title: 'Market Conditions', desc: 'Supply and demand, interest rates and seasonal trends can shift what buyers are willing to pay.' },
  { icon: 'ri-home-4-line', title: 'Size and Layout', desc: 'Square footage, number of bedrooms and bathrooms, floor plan functionality and usable outdoor space.' },
  { icon: 'ri-star-line', title: 'Comparable Sales', desc: 'What similar properties in your area have recently sold for is one of the strongest indicators of market value.' },
  { icon: 'ri-time-line', title: 'Timing', desc: 'How quickly you need to sell and the time of year can influence your pricing strategy.' },
];

const faqs = [
  { q: 'Is this valuation really free?', a: 'Yes. The initial consultation is completely free and comes with no obligation whatsoever. Carol wants you to have clear information before making any decisions.' },
  { q: 'How is this different from an online estimate?', a: 'Online estimates use algorithms that cannot see your home, assess its condition or understand the local market nuances. Carol provides a personal, informed assessment based on real market data and a genuine understanding of your property.' },
  { q: 'How long does the valuation process take?', a: 'The initial consultation typically takes 30–60 minutes. Carol will then prepare a market analysis for you based on comparable sales and current market conditions.' },
  { q: 'Do I need to have my home ready for viewings?', a: 'Not at all. The valuation consultation is just between you and Carol — no buyers, no staging required. She simply needs to see the property to provide an informed opinion.' },
  { q: 'What information will I receive?', a: 'You will receive Carol\'s professional assessment of your property\'s potential market value, an overview of current local market conditions, and practical recommendations for positioning your home if you decide to sell.' },
];

export default function HomeValuation() {
  const [submitted, setSubmitted] = useState(false);
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError('');
    setSubmitting(true);

    const form = e.currentTarget;
    const formData = new FormData(form);

    const hp = formData.get('website_alt');
    if (hp && typeof hp === 'string' && hp.trim() !== '') {
      setSubmitted(true);
      setSubmitting(false);
      return;
    }
    formData.delete('website_alt');

    try {
      const res = await fetch('https://readdy.ai/api/form/d9gfjasugrdi4rjg57g0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(Array.from(formData.entries()).map(([k, v]) => [k, String(v)])).toString(),
      });
      const responseText = await res.text();
      let parsed: any;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const isSpam = typeof serverMsg === 'string' && (serverMsg.toLowerCase().includes('spam') || serverMsg.toLowerCase().includes('form data is spam'));

      if (res.ok && parsed?.code === 'OK' && !isSpam) {
        setSubmitted(true);
        form.reset();
      } else {
        setFormError(isSpam ? 'Your submission could not be processed. Please try again.' : (serverMsg || 'Something went wrong. Please try again.'));
      }
    } catch {
      setFormError('Unable to connect. Please check your internet and try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <Navbar />

      {/* Hero */}
      <section className="relative py-16 md:py-24 bg-foreground-950 overflow-hidden">
        <div className="absolute inset-0 opacity-15">
          <img src="https://readdy.ai/api/search-image?query=Soft%20abstract%20warm%20golden%20beige%20background%20with%20subtle%20texture%20elegant%20minimal%20gradient%20design%20refined%20professional%20aesthetic&width=1920&height=700&seq=val-hero-01&orientation=landscape" alt="" className="w-full h-full object-cover" />
        </div>
        <div className="relative z-10 max-w-[800px] mx-auto px-4 md:px-6 lg:px-10 text-center">
          <p className="text-accent-300 font-body text-sm tracking-widest uppercase mb-3">Home Valuation</p>
          <h2 className="font-heading text-accent-100 text-3xl md:text-4xl lg:text-5xl mb-5">
            Find Out What Your Home Could Sell For
          </h2>
          <p className="text-background-100/70 font-body text-base md:text-lg max-w-[600px] mx-auto mb-8">
            A personal, professional assessment of your property's potential market value — based on real
            local data and a genuine understanding of your home.
          </p>
          <Link to="#valuation-form" className="inline-flex items-center gap-2 bg-primary-500 text-background-50 px-8 py-3.5 rounded-md text-base font-body font-semibold whitespace-nowrap hover:bg-primary-600 transition-all">
            <i className="ri-home-4-line text-lg" />Get Your Free Valuation
          </Link>
        </div>
      </section>

      {/* Why Not Online Estimates */}
      <section className="py-16 md:py-24 bg-background-50">
        <div className="max-w-[1100px] mx-auto px-4 md:px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row gap-10 lg:gap-16 items-center">
            <div className="flex-shrink-0 w-full lg:w-[420px]">
              <div className="aspect-[4/3] rounded-lg overflow-hidden">
                <img src="https://readdy.ai/api/search-image?query=Professional%20real%20estate%20agent%20reviewing%20property%20documents%20at%20a%20modern%20desk%20with%20laptop%20tablet%20and%20market%20reports%20organized%20workspace%20warm%20lighting%20professional%20atmosphere&width=840&height=630&seq=val-why-01&orientation=landscape" alt="Carol reviewing property market data" className="w-full h-full object-cover" />
              </div>
            </div>
            <div className="flex-1">
              <h2 className="font-heading text-foreground-900 text-2xl md:text-3xl lg:text-4xl mb-5">
                Why Online Estimates May Not Tell the Complete Story
              </h2>
              <p className="text-foreground-600 font-body text-sm md:text-base leading-relaxed mb-4">
                Automated online valuation tools can provide a rough estimate, but they cannot see your home.
                They don't know about the kitchen renovation you completed last year, the quiet cul-de-sac
                location or the view from the back deck.
              </p>
              <p className="text-foreground-600 font-body text-sm md:text-base leading-relaxed mb-4">
                These tools rely on algorithms and public data — they cannot assess condition, quality or
                those intangible features that make your property unique. That is why a personal assessment
                from an experienced local professional matters.
              </p>
              <p className="text-foreground-600 font-body text-sm md:text-base leading-relaxed">
                Carol combines market data with a personal understanding of your property to provide an
                assessment that reflects what buyers in your area are actually looking for and willing to pay.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Factors */}
      <section className="py-16 md:py-24 bg-background-100/70">
        <div className="max-w-[1100px] mx-auto px-4 md:px-6 lg:px-10">
          <div className="text-center mb-14">
            <h2 className="font-heading text-foreground-900 text-2xl md:text-3xl lg:text-4xl mb-4">
              Factors That Affect Your Property Value
            </h2>
            <p className="text-foreground-600 font-body text-base max-w-[600px] mx-auto">
              Understanding what influences your home's market value helps you make informed decisions.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {factors.map((f, i) => (
              <div key={i} className="bg-background-50 border border-background-200/70 rounded-lg p-5">
                <div className="w-10 h-10 flex items-center justify-center rounded-md bg-accent-100 mb-4">
                  <i className={`${f.icon} text-accent-700 text-lg`} />
                </div>
                <h3 className="font-heading text-foreground-900 text-base mb-2">{f.title}</h3>
                <p className="text-foreground-600 font-body text-xs leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Valuation Form */}
      <section id="valuation-form" className="py-16 md:py-24 bg-background-50">
        <div className="max-w-[1100px] mx-auto px-4 md:px-6 lg:px-10">
          <div className="text-center mb-12">
            <h2 className="font-heading text-foreground-900 text-2xl md:text-3xl lg:text-4xl mb-4">
              Request Your Free Home Valuation
            </h2>
            <p className="text-foreground-600 font-body text-base max-w-[600px] mx-auto">
              Complete the form below and Carol will be in touch to arrange a convenient time to discuss your property.
              No obligation — just clear, helpful guidance.
            </p>
            <a href="#valuation-faq" className="inline-flex items-center gap-1.5 mt-3 text-primary-600 hover:text-primary-500 font-body text-sm transition-colors group">
              Have questions first?
              <span className="underline">Jump to FAQ</span>
              <i className="ri-arrow-down-line group-hover:translate-y-0.5 transition-transform" />
            </a>
          </div>

          {submitted ? (
            <div className="max-w-[500px] mx-auto text-center py-10">
              <div className="w-16 h-16 flex items-center justify-center rounded-full bg-primary-50 mx-auto mb-6">
                <i className="ri-check-line text-primary-500 text-3xl" />
              </div>
              <h3 className="font-heading text-foreground-900 text-xl mb-3">Thank You!</h3>
              <p className="text-foreground-600 font-body text-sm leading-relaxed">Carol will review your information and get back to you shortly to arrange a convenient time.</p>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              data-readdy-form
              id="home-valuation-form"
              className="max-w-[700px] mx-auto bg-background-50 border border-background-200/70 rounded-xl p-6 md:p-8"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <div>
                  <label className="block text-foreground-700 font-body text-sm font-medium mb-1.5">Full Name *</label>
                  <input type="text" name="name" required className="w-full border border-background-300 rounded-md px-4 py-2.5 text-sm font-body text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent" placeholder="Your full name" />
                </div>
                <div>
                  <label className="block text-foreground-700 font-body text-sm font-medium mb-1.5">Email Address *</label>
                  <input type="email" name="email" required className="w-full border border-background-300 rounded-md px-4 py-2.5 text-sm font-body text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent" placeholder="you@example.com" />
                </div>
                <div>
                  <label className="block text-foreground-700 font-body text-sm font-medium mb-1.5">Telephone Number *</label>
                  <input type="tel" name="phone" required className="w-full border border-background-300 rounded-md px-4 py-2.5 text-sm font-body text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent" placeholder="Your phone number" />
                </div>
                <div>
                  <label className="block text-foreground-700 font-body text-sm font-medium mb-1.5">Property Address *</label>
                  <input type="text" name="address" required className="w-full border border-background-300 rounded-md px-4 py-2.5 text-sm font-body text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent" placeholder="Street address" />
                </div>
                <div>
                  <label className="block text-foreground-700 font-body text-sm font-medium mb-1.5">Property Type</label>
                  <select name="property_type" className="w-full border border-background-300 rounded-md px-4 py-2.5 text-sm font-body text-foreground-900 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent">
                    <option value="">Select property type</option>
                    {propertyTypes.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-foreground-700 font-body text-sm font-medium mb-1.5">Number of Bedrooms</label>
                  <select name="bedrooms" className="w-full border border-background-300 rounded-md px-4 py-2.5 text-sm font-body text-foreground-900 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent">
                    <option value="">Select</option>
                    {[1, 2, 3, 4, 5, 6].map((n) => <option key={n} value={String(n)}>{n} {n === 1 ? 'Bedroom' : 'Bedrooms'}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-foreground-700 font-body text-sm font-medium mb-1.5">Approximate Timeframe</label>
                  <select name="timeframe" className="w-full border border-background-300 rounded-md px-4 py-2.5 text-sm font-body text-foreground-900 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent">
                    <option value="">Select timeframe</option>
                    {timeframes.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-foreground-700 font-body text-sm font-medium mb-1.5">Preferred Contact Method</label>
                  <select name="contact_method" className="w-full border border-background-300 rounded-md px-4 py-2.5 text-sm font-body text-foreground-900 bg-background-50 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent">
                    <option value="">Select</option>
                    <option value="Phone">Phone</option>
                    <option value="Email">Email</option>
                    <option value="Either">Either</option>
                  </select>
                </div>
              </div>
              <div className="mt-5">
                <label className="block text-foreground-700 font-body text-sm font-medium mb-1.5">Message or Additional Information</label>
                <textarea name="message" rows={4} maxLength={500} className="w-full border border-background-300 rounded-md px-4 py-2.5 text-sm font-body text-foreground-900 placeholder-foreground-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent resize-none" placeholder="Tell Carol about your property and any questions..." />
              </div>
              <input type="text" name="website_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="hp-field" />
              <div className="mt-5">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input type="checkbox" name="consent" required className="mt-0.5 w-4 h-4 rounded border-background-300 text-primary-500 focus:ring-primary-400" />
                  <span className="text-foreground-600 font-body text-xs leading-relaxed">I consent to Carol Berry contacting me about my property valuation enquiry. I understand my information will be handled in accordance with applicable privacy regulations.</span>
                </label>
              </div>
              {formError && (
                <div className="mt-4 p-3 bg-primary-50 border border-primary-200 rounded-md">
                  <p className="text-primary-700 font-body text-sm">{formError}</p>
                </div>
              )}
              <button type="submit" disabled={submitting} className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-primary-500 text-background-50 px-8 py-3.5 rounded-md text-base font-body font-semibold whitespace-nowrap hover:bg-primary-600 transition-all disabled:opacity-60">
                {submitting ? <><i className="ri-loader-4-line animate-spin text-lg" />Submitting...</> : <><i className="ri-home-4-line text-lg" />Request My Free Home Valuation</>}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* FAQ */}
      <section id="valuation-faq" className="py-16 md:py-24 bg-background-100/70">
        <div className="max-w-[800px] mx-auto px-4 md:px-6 lg:px-10">
          <h2 className="font-heading text-foreground-900 text-2xl md:text-3xl text-center mb-12">Frequently Asked Questions</h2>
          <div className="space-y-5">
            {faqs.map((faq, i) => (
              <details key={i} className="group border border-background-200/70 rounded-lg">
                <summary className="flex items-center justify-between px-5 py-4 cursor-pointer font-body text-foreground-800 text-sm md:text-base font-medium">
                  {faq.q}
                  <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform" />
                </summary>
                <div className="px-5 pb-4">
                  <p className="text-foreground-600 font-body text-sm leading-relaxed">{faq.a}</p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}